import { useState, useEffect, useCallback } from "react";
import apiFetch from "../../../../shared/services/apiClient";

/**
 * useCatalogo
 * Hook principal para el catálogo de cuentas contable.
 *
 * Expone:
 *   arbol         → cuentas raíz con subcuentas anidadas (para TreeView)
 *   detalle       → solo cuentas is_detalle=true (para selects de facturas)
 *   loading       → estado de carga
 *   crear(data)   → POST: crear subcuenta bajo un padre
 *   editar(id, d) → PATCH: cambiar nombre/descripción/is_detalle
 *   desactivar(id)→ DELETE (soft): marcar como inactiva
 *   activar(id)   → POST activar
 *   refetch()     → recargar todo
 */
export function useCatalogo() {
  const [arbol,   setArbol]   = useState([]);
  const [detalle, setDetalle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const cargarArbol = useCallback(async () => {
    try {
      const res  = await apiFetch("/api/finance/catalogo/arbol/");
      const data = await res.json();
      setArbol(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Error al cargar el catálogo");
    }
  }, []);

  const cargarDetalle = useCallback(async () => {
    try {
      const res  = await apiFetch("/api/finance/catalogo/detalle/");
      const data = await res.json();
      setDetalle(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Error al cargar cuentas de detalle");
    }
  }, []);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([cargarArbol(), cargarDetalle()]);
    setLoading(false);
  }, [cargarArbol, cargarDetalle]);

  useEffect(() => { refetch(); }, [refetch]);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  async function crear(data) {
    const res = await apiFetch("/api/finance/catalogo/", {
      method: "POST",
      body:   JSON.stringify(data),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(JSON.stringify(body));
    invalidarCacheDetalle();
    await refetch();
    return body;
  }

  async function editar(id, data) {
    const res = await apiFetch(`/api/finance/catalogo/${id}/`, {
      method: "PATCH",
      body:   JSON.stringify(data),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(JSON.stringify(body));
    invalidarCacheDetalle();
    await refetch();
    return body;
  }

  // Solicitar advertencia antes de eliminar (sin confirmar=1)
  async function previewEliminar(id) {
    const res  = await apiFetch(`/api/finance/catalogo/${id}/`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (res.status === 200 && data.requiere_confirmacion) return data;
    throw new Error(data.detail || "Error al obtener advertencia");
  }

  // Eliminar definitivamente (con confirmar=1)
  async function eliminar(id) {
    const res = await apiFetch(`/api/finance/catalogo/${id}/?confirmar=1`, {
      method: "DELETE",
    });
    if (!res.ok && res.status !== 204) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || "Error al eliminar la cuenta");
    }
    await refetch();
  }

  return { arbol, detalle, loading, error, crear, editar, previewEliminar, eliminar, refetch };
}

// Caché en memoria por sesión: evita múltiples requests iguales
// cuando varios componentes montan useCuentasDetalle al mismo tiempo.
const _detalleCache = new Map();   // url → { data, ts }
const _CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

async function _fetchDetalle(url) {
  const cached = _detalleCache.get(url);
  if (cached && Date.now() - cached.ts < _CACHE_TTL_MS) {
    return cached.data;
  }
  const res  = await apiFetch(url);
  const data = await res.json();
  const arr  = Array.isArray(data) ? data : [];
  _detalleCache.set(url, { data: arr, ts: Date.now() });
  return arr;
}

/** Invalida el caché (llamar después de crear/editar cuentas). */
export function invalidarCacheDetalle() {
  _detalleCache.clear();
}

/**
 * useCuentasDetalle
 * Hook ligero solo para obtener cuentas de detalle (para selects en facturas).
 * Acepta filtro opcional por tipo: "INGRESO" | "GASTO" | etc.
 * Usa caché en memoria 5 min para no repetir la misma request en cada render.
 */
export function useCuentasDetalle(tipo = null) {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const url = tipo
      ? `/api/finance/catalogo/detalle/?tipo=${tipo}`
      : "/api/finance/catalogo/detalle/";

    _fetchDetalle(url)
      .then((arr) => { if (!cancelled) setCuentas(arr); })
      .catch(() => { if (!cancelled) setCuentas([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [tipo]);

  return { cuentas, loading };
}
